import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Divider, Grid } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import {
  Calendar,
  PuiCheckbox,
  PuiSelect,
  PuiTextField,
  TextWithTooltip,
} from '@pbt/pbt-ui-components'

import NumericInput from '~/components/common/inputs/NumericInput'
import Typography from '~/components/elements/Typography/Typography'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getInventoryDamageReasons } from '~/store/reducers/constants'

import type { ReceiptFields } from './hooks/useReceiptFields'

interface OrderItemReceiptProps {
  fields: ReceiptFields
  isDraft?: boolean
  orderedQuantity: number
  perPackageAmount: number
  perPackageLabel: string
  productSizeUnit: string
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.colors.grayGray3}`,
  backgroundColor: theme.colors.grayWhite,
}))

export const OrderItemReceipt = ({
  fields,
  orderedQuantity,
  perPackageLabel,
  perPackageAmount,
  productSizeUnit,
  isDraft = false,
}: OrderItemReceiptProps) => {
  const theme = useTheme()
  const { t } = useTranslation('Admin')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const InventoryDamageReasons = useSelector(getInventoryDamageReasons)

  const {
    receivedQuantity,
    lotNumber,
    expirationDate,
    serialNumber,
    businessReferenceNumber,
    damaged,
    damageReasonId,
    damageNotes,
  } = fields

  // Set damage reason id to first option in list if it's not already set on draft receipt
  useEffect(() => {
    if (isDraft && damaged.value && !damageReasonId.value) {
      damageReasonId.setValue(InventoryDamageReasons[0].id)
    }
  }, [isDraft, damaged.value, damageReasonId.value])

  const receivedQuantityHasError = receivedQuantity.errors.length > 0
  const receivedQuantityHelperText = receivedQuantityHasError
    ? receivedQuantity.message
    : receivedQuantity.value
      ? `${receivedQuantity.value * perPackageAmount} ${productSizeUnit}`
      : ' ' // Default whitespace to prevent layout shift

  const inputDisabled = !isDraft || isUpdating

  const isReceivedQuantityOverage = receivedQuantity.value > orderedQuantity
  const showOverageWarning = isDraft && isReceivedQuantityOverage
  const inputLabelColor = showOverageWarning
    ? theme.colors.uiNeedsAttention
    : undefined

  return (
    <StyledGrid container gap={2} p={2}>
      <Grid item gap={1} xs={2.5}>
        <Box alignItems="center" display="flex" gap={1}>
          <NumericInput
            autoFocus
            required
            FormHelperTextProps={{
              sx: {
                color: inputLabelColor,
              },
            }}
            InputLabelProps={{
              sx: {
                color: inputLabelColor,
                '&.Mui-focused': {
                  color: inputLabelColor,
                },
              },
            }}
            TooltipProps={{
              open: showOverageWarning,
              message: t('Admin:CATALOG.ORDERS.GREATER_THAN_QUANTITY_ORDERED'),
              errorColor: theme.colors.uiNeedsAttention,
            }}
            disabled={inputDisabled}
            field={receivedQuantity}
            helperText={receivedQuantityHelperText}
            label={receivedQuantity.label}
            margin="none"
          />
          <Typography.Paragraph sx={{ whiteSpace: 'nowrap' }}>
            {perPackageLabel}
          </Typography.Paragraph>
        </Box>
      </Grid>
      <Grid item xs>
        <PuiTextField
          disabled={inputDisabled}
          field={lotNumber}
          label={lotNumber.label}
          margin="none"
        />
      </Grid>
      <Grid item xs>
        <Calendar
          fullWidth
          disabled={inputDisabled}
          field={expirationDate}
          label={expirationDate.label}
          margin="none"
        />
      </Grid>
      <Grid item xs>
        <PuiTextField
          disabled={inputDisabled}
          field={serialNumber}
          label={serialNumber.label}
          margin="none"
        />
      </Grid>
      <Grid item xs>
        <PuiTextField
          disabled={inputDisabled}
          field={businessReferenceNumber}
          label={businessReferenceNumber.label}
          margin="none"
        />
      </Grid>
      <Grid item alignItems="center" alignSelf="center" display="flex">
        <PuiCheckbox
          disabled={inputDisabled}
          field={damaged}
          label={damaged.label}
        />
        <TextWithTooltip
          TooltipProps={{ PopperProps: { sx: { maxWidth: '150px' } } }}
          tooltipText={t('Tooltips:DAMAGED_INVENTORY_ORDER_ITEM_TOOLTIP_TEXT')}
        />
      </Grid>
      {damaged.value && (
        <>
          <Divider orientation="horizontal" sx={{ width: '100%' }} />
          <Grid container item alignItems="flex-end" gap={4}>
            <Grid item py={1} xs={2}>
              <PuiSelect
                fullWidth
                disabled={inputDisabled}
                field={damageReasonId}
                items={InventoryDamageReasons}
                label={damageReasonId.label}
              />
            </Grid>
            <Grid item xs>
              <PuiTextField
                disabled={inputDisabled}
                field={damageNotes}
                label={damageNotes.label}
              />
            </Grid>
          </Grid>
        </>
      )}
    </StyledGrid>
  )
}
